import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { CheckCircle, Loader2, XCircle, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { toast } = useToast();
  const { language } = useLanguage();
  const { login } = useAuth();
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null); // 'success' | 'error' | 'expired'
  const [errorMessage, setErrorMessage] = useState('');
  const hasVerified = useRef(false); // Prevent double verification in StrictMode

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent double execution
      if (hasVerified.current) return;
      hasVerified.current = true;
      
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage(language === 'nl' ? 'Geen verificatietoken gevonden' : 'No verification token found');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/verify-email/${token}`);
        const data = await response.json();
        
        if (!response.ok) {
          if (data.detail?.includes('verlopen') || data.detail?.includes('expired')) {
            setVerificationStatus('expired');
          } else {
            setVerificationStatus('error');
          }
          setErrorMessage(data.detail || (language === 'nl' ? 'Verificatie mislukt' : 'Verification failed'));
        } else {
          setVerificationStatus('success');
          
          // Auto-login the user
          if (data.token && data.user) {
            localStorage.setItem('roomhub_token', data.token);
            localStorage.setItem('roomhub_user', JSON.stringify(data.user));
            login(data.user);
          }
        }
      } catch (error) {
        console.error('Verification error:', error);
        setVerificationStatus('error');
        setErrorMessage(language === 'nl' ? 'Er ging iets mis bij het verifiëren' : 'Something went wrong during verification');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token]); // Only depend on token

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12 pt-24">
          <Card className="w-full max-w-md rounded-3xl shadow-xl border-0">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-[#2E7D32] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#2A2A2A] mb-2">
                {language === 'nl' ? 'Email verifiëren...' : 'Verifying email...'}
              </h2>
              <p className="text-[#9E9E9E]">
                {language === 'nl' ? 'Even geduld alsjeblieft' : 'Please wait'}
              </p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Success state
  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
        <Toaster />
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12 pt-24">
          <Card className="w-full max-w-md rounded-3xl shadow-xl border-0 overflow-hidden">
            <div className="bg-gradient-to-r from-[#2E7D32] to-[#4CAF50] p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {language === 'nl' ? 'Email geverifieerd!' : 'Email verified!'}
              </h1>
              <p className="text-white/80">
                {language === 'nl' ? 'Je account is nu actief' : 'Your account is now active'}
              </p>
            </div>
            <CardContent className="p-8 text-center">
              <p className="text-[#2A2A2A] mb-6">
                {language === 'nl' 
                  ? 'Welkom bij Swipe je woning! Je kunt nu beginnen met swipen naar je droomwoning.'
                  : 'Welcome to Swipe je woning! You can now start swiping for your dream home.'}
              </p>
              <Button 
                onClick={() => navigate('/swipematch')}
                className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold rounded-xl"
              >
                {language === 'nl' ? 'Start met swipen' : 'Start swiping'}
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Expired token state
  if (verificationStatus === 'expired') {
    return (
      <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
        <Toaster />
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12 pt-24">
          <Card className="w-full max-w-md rounded-3xl shadow-xl border-0">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#2A2A2A] mb-2">
                {language === 'nl' ? 'Link verlopen' : 'Link expired'}
              </h1>
              <p className="text-[#9E9E9E] mb-6">
                {language === 'nl' 
                  ? 'Deze verificatielink is verlopen. Vraag een nieuwe aan via de inlogpagina.'
                  : 'This verification link has expired. Request a new one via the login page.'}
              </p>
              <Link to="/login">
                <Button className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold rounded-xl">
                  {language === 'nl' ? 'Naar inloggen' : 'Go to login'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
      <Toaster />
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12 pt-24">
        <Card className="w-full max-w-md rounded-3xl shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#2A2A2A] mb-2">
              {language === 'nl' ? 'Verificatie mislukt' : 'Verification failed'}
            </h1>
            <p className="text-[#9E9E9E] mb-6">
              {errorMessage || (language === 'nl' ? 'Er ging iets mis met de verificatie.' : 'Something went wrong with the verification.')}
            </p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold rounded-xl">
                  {language === 'nl' ? 'Naar inloggen' : 'Go to login'}
                </Button>
              </Link>
              <Link to="/registreren">
                <Button variant="outline" className="w-full h-12 rounded-xl">
                  {language === 'nl' ? 'Opnieuw registreren' : 'Register again'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default EmailVerificationPage;
